/**
 * Workflow and document visibility rules
 * Centralizes business logic for conditional rendering based on ticket types and document states
 */

import { ValidTicketTypes } from '../hooks/useTicketTypeHook';

/**
 * Checks if tax state has pending signature documents
 * @param {Object} taxState - Tax state object containing additional_docs
 * @returns {boolean} - True if signature documents exist and are not completed
 */
export const hasSignatureDocuments = (taxState) => {
    return taxState?.additional_docs?.some(doc => 
        doc.document_type.endsWith('_SignaturePage') && !doc.is_completed
    ) || false;
};

/**
 * Gets filtered list of pending signature documents
 * @param {Object} taxState - Tax state object containing additional_docs
 * @returns {Array} - Array of pending signature documents
 */
export const getSignatureDocuments = (taxState) => {
    return taxState?.additional_docs?.filter(doc => 
        doc.document_type.endsWith('_SignaturePage') && !doc.is_completed
    ) || [];
};

/**
 * Checks if tax state has POM (Proof of Mailing) documents
 * @param {Object} taxState - Tax state object containing additional_docs
 * @returns {boolean} - True if POM documents exist
 */
export const hasPOMDocuments = (taxState) => {
    return taxState?.additional_docs?.some(doc => 
        doc.document_type.startsWith('POM')
    ) || false;
};

/**
 * Checks if tax state has non-signature/non-POM additional documents
 * @param {Object} taxState - Tax state object containing additional_docs
 * @returns {boolean} - True if other additional documents exist
 */
export const hasOtherAdditionalDocuments = (taxState) => {
    return taxState?.additional_docs?.filter(doc => 
        !doc.document_type.startsWith('POM') && 
        !doc.document_type.endsWith('_SignaturePage')
    ).length > 0;
};

/**
 * Determines if signature section should be displayed
 * @param {Object} taxState - Tax state object
 * @param {string} section - Current section name
 * @returns {boolean} - True if signature section should be shown
 */
export const shouldShowSignatureSection = (taxState, section) => {
    if (!taxState || section !== 'documents') {
        return false;
    }

    const ticketType = (taxState.ticket_type || '').toLowerCase();
    const validTicketTypes = [ValidTicketTypes.RTEDR, ValidTicketTypes.RTE];
    
    return hasSignatureDocuments(taxState) && validTicketTypes.includes(ticketType);
};

/**
 * Checks if ticket is in payment state (excluding prepare payment)
 * @param {Object} taxState - Tax state object
 * @returns {boolean} - True if in active payment state
 */
export const isInPaymentState = (taxState) => {
    if (!taxState?.ticketState) {
        return false;
    }
    return taxState.ticketState.includes('_PAYMENT') && !taxState.ticketState.includes('_PREPARE_PAYMENT');
};

/**
 * Checks if ticket is in prepare payment state
 * @param {Object} taxState - Tax state object
 * @returns {boolean} - True if in prepare payment state
 */
export const isInPreparePaymentState = (taxState) => {
    return taxState?.ticketState?.includes('_PREPARE_PAYMENT') || false;
};

/**
 * Checks if ticket is in file/filing state
 * @param {Object} taxState - Tax state object
 * @returns {boolean} - True if in file state
 */
export const isInFileState = (taxState) => {
    return taxState?.ticketState?.includes('_FILE') || false;
};

/**
 * Determines if payment section should be displayed
 * @param {Object} taxState - Tax state object
 * @returns {boolean} - True if payment section should be shown
 */
export const shouldShowPaymentSection = (taxState) => {
    return isInPaymentState(taxState) || hasPOMDocuments(taxState);
};

/**
 * Configuration for extending with new ticket types
 * Add new ticket types and their supported features here
 */
export const TicketTypeFeatures = {
    [ValidTicketTypes.RTEDR]: {
        supportsSignatureDocuments: true,
        supportsPayment: true,
        supportsDocumentUpload: true
    },
    [ValidTicketTypes.RTE]: {
        supportsSignatureDocuments: true,
        supportsPayment: true,
        supportsDocumentUpload: true
    },
    [ValidTicketTypes.VIR]: {
        supportsSignatureDocuments: false,
        supportsPayment: true,
        supportsDocumentUpload: true
    },
    [ValidTicketTypes.VDO]: {
        supportsSignatureDocuments: false,
        supportsPayment: false,
        supportsDocumentUpload: true
    }
};

/**
 * Checks if a ticket type supports a specific feature
 * @param {string} ticketType - Ticket type (e.g., 'rtedr', 'vir')
 * @param {string} feature - Feature name (e.g., 'supportsSignatureDocuments')
 * @returns {boolean} - True if ticket type supports the feature
 */
export const ticketTypeSupportsFeature = (ticketType, feature) => {
    const normalizedType = (ticketType || '').toLowerCase();
    const config = TicketTypeFeatures[normalizedType];
    return config ? config[feature] || false : false;
};

/**
 * Gets user-friendly display name for document forms
 * Converts technical form identifiers to localized display names
 * @param {string} formName - Technical form name (e.g., 'POMW', 'T1032T', 'Ivan_SignaturePage')
 * @param {string} lang - Language code ('en' or 'fr')
 * @returns {string} - Localized display name
 */
export const getDocumentDisplayName = (formName, lang = 'en') => {
    if (!formName) {
        return 'Form';
    }

    const upperFormName = formName.toUpperCase();

    // POM forms
    if (upperFormName === 'POMW') {
        return lang === 'fr' 
            ? 'Tranquillité d\'esprit – Accord et contrat' 
            : 'Peace of Mind – Waiver';
    }
    
    if (upperFormName === 'POMA' || upperFormName === 'POMQCA') {
        return lang === 'fr' 
            ? 'Tranquillité d\'esprit – Accord et contrat' 
            : 'Peace of Mind – Agreement and Contract';
    }

    // T1032 forms (all variants normalize to T1032)
    if (upperFormName === 'T1032W' || upperFormName === 'T1032T' || upperFormName === 'T1032R') {
        return 'T1032';
    }

    // Signature Page forms (handles patterns like "Ivan_SignaturePage")
    if (upperFormName.includes('_SIGNATUREPAGE')) {
        return lang === 'fr' 
            ? 'Demande d\'autorisation - Page de signature' 
            : 'Authorization Request - Signature Page';
    }

    return formName;
};
